// src/components/dashboard/AccountDetailsCard.jsx
import React, { useState } from "react";
import api from "../../utils/api";
import { API_CONFIG } from "../../utils/apiConfig";
import { showToast } from "../../utils/helpers";

const AccountDetailsCard = ({ user, accountDetails, shareAccountDetails, shareViaEmail, shareViaWhatsApp }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (isDownloading) return;
    
    setIsDownloading(true);
    
    try {
      const response = await api.get(API_CONFIG.DOWNLOAD.BANK_DETAILS_PDF, {
        responseType: "blob",
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      
      const contentDisposition = response.headers["content-disposition"];
      let fileName = "bank-account-details.pdf";
      
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch.length === 2) {
          fileName = fileNameMatch[1];
        }
      }
      
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      
      link.remove();
      window.URL.revokeObjectURL(url);
      
      showToast("PDF downloaded successfully", "success");
      
    } catch (error) {
      console.error("Download failed:", error);
      
      if (error.response?.status === 404) {
        showToast("Download service is temporarily unavailable", "error");
      } else {
        showToast("Failed to download PDF. Please try again.", "error");
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleClientSideDownload = () => {
    setIsDownloading(true);
    
    const accountDetailsText = `
      Bank Account Details
      ====================
      
      Account Holder Name: ${accountDetails?.account_name || 'N/A'}
      Account Number: ${accountDetails?.account_number || 'N/A'}
      Bank Name: ${accountDetails?.bank_name || 'N/A'}
      
      Generated on: ${new Date().toLocaleDateString()}
    `;
    
    const blob = new Blob([accountDetailsText], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "bank-account-details.txt");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    showToast("Account details downloaded as text file", "success");
    setIsDownloading(false);
  };

  return (
    <div className="w-full bg-gradient-to-br from-purple-600 to-blue-600 text-white rounded-2xl lg:rounded-3xl shadow-lg overflow-hidden relative">
      {/* Watermark */}
      <div className="absolute opacity-5 top-4 left-4 lg:top-10 lg:left-10 transform -rotate-12 z-0">
        <img
          src={`${process.env.PUBLIC_URL}/assets/logo_white.png`}
          alt="Watermark"
          className="w-20 h-20 lg:w-40 lg:h-40 object-contain"
        />
      </div>

      <div className="relative z-10">
        {/* Header */}
        <div className="p-4 lg:p-6 bg-opacity-10 border-b border-white border-opacity-20 text-center">
          <h3 className="text-lg lg:text-xl font-semibold text-white">Bank Account Details</h3>
        </div>

        {/* Account Details */}
        <div className="p-4 lg:p-6 space-y-3 lg:space-y-4">
          <div className="flex flex-col lg:flex-row lg:justify-between py-2 border-b border-white border-opacity-20">
            <span className="text-xs lg:text-sm opacity-80 mb-1 lg:mb-0">Account Holder Name</span>
            <span className="text-sm lg:text-base font-medium">
              {accountDetails?.account_name || 'N/A'}
            </span>
          </div>

          <div className="flex flex-col lg:flex-row lg:justify-between py-2 border-b border-white border-opacity-20">
            <span className="text-xs lg:text-sm opacity-80 mb-1 lg:mb-0">Account Number</span>
            <span className="text-sm lg:text-base font-medium">
              {accountDetails?.account_number || 'N/A'}
            </span>
          </div>

          <div className="flex flex-col lg:flex-row lg:justify-between py-2 border-b border-white border-opacity-20">
            <span className="text-xs lg:text-sm opacity-80 mb-1 lg:mb-0">Bank Name</span>
            <span className="text-sm lg:text-base font-medium">{accountDetails?.bank_name || 'N/A'}</span>
          </div>
        </div>

        {/* Download & Share Section */}
        <div className="p-4 lg:p-6 bg-white bg-opacity-5">
          <div className="flex flex-col gap-3">
            {/* Download Button */}
            <button
              className="w-full px-4 py-3 bg-white text-purple-600 rounded-lg font-medium hover:bg-gray-100 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed text-sm lg:text-base"
              onClick={handleDownload}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Downloading...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                  Download PDF
                </>
              )}
            </button>

            {/* Share Buttons */}
            <div className="flex gap-2 justify-center">
              <button
                onClick={shareAccountDetails}
                className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-xs bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-colors"
                title="Share Account Details"
              >
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M11 2.5a2.5 2.5 0 1 1 .603 1.628l-6.718 3.12a2.499 2.499 0 0 1 0 1.504l6.718 3.12a2.5 2.5 0 1 1-.488.876l-6.718-3.12a2.5 2.5 0 1 1 0-3.256l6.718-3.12A2.5 2.5 0 0 1 11 2.5"/>
                </svg>
                Share
              </button>
              <button
                onClick={shareViaWhatsApp}
                className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-xs bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                title="Share via WhatsApp"
              >
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.050-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.729.729 0 0 0-.529.247c-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z"/>
                </svg>
                WhatsApp
              </button>
              <button
                onClick={shareViaEmail}
                className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-xs bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-colors"
                title="Share via Email"
              >
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z"/>
                </svg>
                Email
              </button>
            </div>

            {/* Help Text */}
            <p className="text-xs opacity-70 text-center mt-2">
              Having issues with PDF? Try the text version.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountDetailsCard;